import { cn } from '@invent/wl-ui-kit-next';
import {
  Button,
  Checkbox,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@invent/wl-ui-kit-next/ui';
import { Filter } from 'lucide-react';
import * as React from 'react';

import { listsApi } from '../../api/lists-api';
import type {
  IListCriteriaGroup,
  IListFacets,
  IListFacetsRequest,
  IListField,
  IListQuery
} from '../../api/lists-contracts';
import {
  describeNode,
  emptyFunnel,
  funnelFilters,
  funnelNodes,
  funnelOf,
  leafTest,
  rootChips,
  rootLeafFor,
  TEXT_FUNNEL_OPS,
  valueWords,
  withRootLeaf,
  writeCriteria,
  type FunnelState,
  type ICriteriaGroup,
  type ICriteriaWords,
  type KindOf,
  type TextFunnelOp
} from '../../list-criteria';
import { formatListValue, operatorArity, operatorLabel } from '../../lists';
import { refusalMessage } from '../../refusal';
import { DateLiteral } from './date-literal';

/**
 * A column's funnel — Conga's Smart Filter, which is how the research team filters (SR-230, design
 * `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md` §3.4; SR-221's grid note §2). The
 * column's kind decides its shape (`leafTest`): ticked values with their counts, a range, dates,
 * or — for a long text, too long a value to tick (SR-408's R4) — one text test and its
 * box; and a summary filters on nothing, which the header says.
 *
 * **It edits the root `and` group's own nodes on its column and nothing else** (`withRootLeaf`), so
 * the funnels, the chips and *Advanced…* are one tree. A condition on the column inside a nested
 * group — or a root one the funnel cannot draw (`contains`, a `gt`) — is shown read-only in words,
 * with "Set in Advanced…".
 *
 * **The values are survey-api's** (`POST /lists/facets`), counted under the query **without this
 * column's own condition**, so a value excluded once can be ticked back — the demo study's rule,
 * now the server's. A search narrows them there (`q`); "(Blank)" is the facet's `null`; past 500
 * values survey-api says `truncated`, and so does the funnel. A cold column can take most of a
 * second, so the wait is said — "Counting…" — never a spinner alone (§0.6).
 *
 * What is ticked is a draft until **Apply**: a filter that moved on every tick would read the rows
 * under the open popover, and Conga's own funnel waits for its button the same way. The funnel
 * judges nothing: a range box holds what was typed, and survey-api's 400 lands on the chip.
 *
 * **Whether it is open is the screen's** (`open`), so a filter chip's press opens its column's
 * funnel (§3.4): the column is brought into the grid's view first, and the chip (`returnTo`) takes
 * the focus back as the funnel closes — not the header's button, a row of chips away — unless a
 * press outside closed it, which keeps the focus where it went.
 */
export function Funnel({
  field,
  root,
  query,
  kindOf,
  words,
  onApply,
  onAdvanced,
  open,
  onOpenChange,
  returnTo,
  base,
  facets
}: {
  field: IListField;
  root: ICriteriaGroup;
  /** The screen's query — survey, scope, columns; its criteria are replaced by the tree's. */
  query: IListQuery;
  kindOf: KindOf;
  words: ICriteriaWords;
  onApply: (root: ICriteriaGroup) => void;
  /** Opens *Advanced…* — the way to a condition the funnel only shows. */
  onAdvanced: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The chip that opened it, or null for the header's own button. */
  returnTo: Nullable<HTMLElement>;
  /** A work page's own conditions — its tab, quick views, search — ANDed with the tree's where the
   *  values are counted; Lists has none. */
  base?: Nullable<IListCriteriaGroup>;
  /** Where the values are read — a work page's own facets route; Lists' by default. */
  facets?: FacetsRead;
}) {
  const trigger = React.useRef<HTMLButtonElement>(null);
  // The chip to hand the focus back to as the funnel closes: held from the opening (the close
  // clears `returnTo` with `open`), and dropped by a press outside, which keeps the focus where it
  // went. Set on every opening — one during the exit animation mounts nothing anew.
  const opener = React.useRef<Nullable<HTMLElement>>(null);
  React.useLayoutEffect(() => {
    if (!open) return;
    opener.current = returnTo;
    if (returnTo) trigger.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [open, returnTo]);
  if (!hasFunnel(field)) {
    return <span className="text-xs font-normal text-muted-foreground">Not filterable</span>;
  }
  const test = leafTest(field.kind);

  const current = rootLeafFor(root, field.code);
  const state = current && !current.nested ? funnelOf(current.nodes, field.kind) : emptyFunnel(test);
  // The column's own chip — never the name box's, which is on Name's field and not its funnel's.
  const chip = rootChips(root, words).find(
    (one) => one.door === 'funnel' && one.field === field.code
  );
  const said = current
    ? (chip?.words ?? current.nodes.map((node) => describeNode(node, words)).join(' and '))
    : null;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          ref={trigger}
          type="button"
          aria-label={said ? `Filter ${field.label}: ${said}` : `Filter ${field.label}`}
          data-funnel={field.code}
          data-active={said ? '' : undefined}
          className={cn(
            'inline-flex size-6 shrink-0 items-center justify-center rounded outline-none hover:bg-[var(--accent)] focus-visible:ring-2 focus-visible:ring-ring',
            said ? 'text-[var(--color-teal)]' : 'text-[var(--veritant-royal-55)]'
          )}
        >
          <Filter className={cn('size-3.5', said && 'fill-current')} aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        aria-label={`Filter ${field.label}`}
        className="grid w-80 gap-3 p-3 text-[0.8125rem] font-normal"
        onInteractOutside={(event) => {
          // The chip that opened it is its second trigger: a press there is the chip's to answer
          // (it closes the funnel), not a dismissal that the click would reopen.
          if (opener.current?.contains(event.target as Node)) {
            event.preventDefault();
            return;
          }
          opener.current = null;
        }}
        onCloseAutoFocus={(event) => {
          const target = opener.current;
          if (!target?.isConnected) return;
          event.preventDefault();
          target.focus();
        }}
      >
        {state === null || current?.nested ? (
          <div className="grid gap-2">
            <p className="font-medium text-[var(--color-royal-blue)]">{said ?? 'Not filtered'}</p>
            <p className="text-muted-foreground">
              {current?.nested
                ? 'This column is filtered inside a group of conditions.'
                : 'This condition is not one the funnel draws.'}
            </p>
            <Button
              type="button"
              variant="outline"
              className="h-7 w-fit px-2.5"
              onClick={() => {
                onOpenChange(false);
                onAdvanced();
              }}
            >
              Set in Advanced…
            </Button>
          </div>
        ) : (
          <FunnelForm
            field={field}
            initial={state}
            root={root}
            query={query}
            kindOf={kindOf}
            words={words}
            base={base}
            facets={facets}
            onApply={(next) => {
              onApply(next);
              onOpenChange(false);
            }}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}

/** Whether a column draws a funnel: a summary, or a field survey-api says filters on nothing,
 *  says "Not filterable" in its place — and a chip on it leads to *Advanced…* instead. */
export function hasFunnel(field: IListField): boolean {
  return leafTest(field.kind) !== 'none' && field.filterable;
}

function FunnelForm({
  field,
  initial,
  root,
  query,
  kindOf,
  words,
  base,
  facets,
  onApply
}: {
  field: IListField;
  initial: FunnelState;
  root: ICriteriaGroup;
  query: IListQuery;
  kindOf: KindOf;
  words: ICriteriaWords;
  base?: Nullable<IListCriteriaGroup>;
  facets?: FacetsRead;
  onApply: (root: ICriteriaGroup) => void;
}) {
  const [draft, setDraft] = React.useState<FunnelState>(initial);
  const apply = (event: React.FormEvent) => {
    event.preventDefault();
    const nodes = funnelFilters(draft) ? funnelNodes(field.code, field.kind, draft) : null;
    onApply(withRootLeaf(root, field.code, nodes));
  };
  return (
    <form onSubmit={apply} className="grid gap-3">
      {draft.test === 'values' && (
        <SmartFilter
          field={field}
          draft={draft}
          root={root}
          query={query}
          kindOf={kindOf}
          words={words}
          base={base}
          facets={facets}
          onDraft={setDraft}
        />
      )}
      {draft.test === 'range' && <RangeFilter field={field} draft={draft} onDraft={setDraft} />}
      {draft.test === 'date' && <DateFilter draft={draft} onDraft={setDraft} />}
      {draft.test === 'text' && <TextFilter draft={draft} onDraft={setDraft} />}
      <div className="flex justify-end gap-2 border-t border-border pt-2">
        <Button
          type="button"
          variant="ghost"
          className="h-7 px-2.5"
          onClick={() => onApply(withRootLeaf(root, field.code, null))}
        >
          Clear
        </Button>
        <Button type="submit" className="h-7 px-3">
          Apply
        </Button>
      </div>
    </form>
  );
}

type ValuesState = Extract<FunnelState, { test: 'values' }>;

/** A funnel's values read — `POST /lists/facets`'s request and answer. */
export type FacetsRead = (body: IListFacetsRequest, signal: AbortSignal) => Promise<IListFacets>;

/** The tree's criteria ANDed under a work page's `base`, each a nested group — the tree's as is
 *  without one, and an empty group left out, which survey-api reads as every row only at the root. */
function withBase(base: Nullable<IListCriteriaGroup> | undefined, tree: IListCriteriaGroup): IListCriteriaGroup {
  if (!base) return tree;
  const parts = [base, tree].filter((group) => group.children.length > 0);
  return {
    version: tree.version,
    op: 'and',
    children: parts.map((group) => ({ op: group.op, children: group.children }))
  };
}

/** A facet value's key — the value as JSON, so `true`, `"true"` and `1` stay three values — and
 *  a text's in lower case: survey-api's filter and facets ignore its case (SR-417), so a view's
 *  `ny` ticks the `NY` the facets offer rather than standing beside it. */
const keyIn = (kind: IListField['kind']) => (value: unknown) =>
  JSON.stringify(kind === 'text' && typeof value === 'string' ? value.toLowerCase() : value) ?? 'null';

type FacetRead =
  | { state: 'reading' }
  | { state: 'failed'; message: string }
  | { state: 'read'; facets: IListFacets };

function SmartFilter({
  field,
  draft,
  root,
  query,
  kindOf,
  words,
  base,
  facets: readFacets = listsApi.facets,
  onDraft
}: {
  field: IListField;
  draft: ValuesState;
  root: ICriteriaGroup;
  query: IListQuery;
  kindOf: KindOf;
  words: ICriteriaWords;
  base?: Nullable<IListCriteriaGroup>;
  facets?: FacetsRead;
  onDraft: (state: ValuesState) => void;
}) {
  const keyOf = keyIn(field.kind);
  const [search, setSearch] = React.useState('');
  const [q, setQ] = React.useState('');
  const [attempt, setAttempt] = React.useState(0);
  const [read, setRead] = React.useState<FacetRead>({ state: 'reading' });

  React.useEffect(() => {
    const timer = window.setTimeout(() => setQ(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  // Counted under the query without this column's own condition: a value excluded stays offered.
  const facetQuery = React.useMemo<IListQuery>(
    () => ({ ...query, criteria: withBase(base, writeCriteria(withRootLeaf(root, field.code, null), kindOf)) }),
    [query, root, field.code, kindOf, base]
  );
  const facetKey = JSON.stringify(facetQuery);

  React.useEffect(() => {
    const controller = new AbortController();
    setRead({ state: 'reading' });
    readFacets({ query: facetQuery, field: field.code, ...(q === '' ? {} : { q }) }, controller.signal)
      .then((answer) => setRead({ state: 'read', facets: answer }))
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setRead({ state: 'failed', message: refusalMessage(error, 'The values could not be read.') });
      });
    return () => controller.abort();
    // `facetKey` stands for `facetQuery`, whose object is rebuilt on every render of the page.
  }, [facetKey, field.code, q, attempt]);

  const picked = new Set(draft.values.map(keyOf));
  const facets = read.state === 'read' ? read.facets.values : [];
  const offered = facets.filter((facet) => facet.value !== null);
  const blankOffered = facets.some((facet) => facet.value === null);
  // A value ticked that the read does not offer — searched away, or held by no row now — stays
  // listed, so it can be unticked.
  const shownKeys = offered.map((facet) => keyOf(facet.value));
  const strays =
    read.state === 'read' ? draft.values.filter((value) => !shownKeys.includes(keyOf(value))) : [];
  const all =
    (offered.length > 0 || blankOffered) &&
    shownKeys.every((key) => picked.has(key)) &&
    (!blankOffered || draft.blank);

  const setValues = (values: unknown[]) => onDraft({ ...draft, values });
  const toggle = (value: unknown, on: boolean) =>
    setValues(on ? [...draft.values, value] : draft.values.filter((one) => keyOf(one) !== keyOf(value)));
  const labelOf = (value: unknown, label: Nullable<string>) =>
    label ?? (formatListValue(field, value) || valueWords(words, field.code, value));

  return (
    <div className="grid gap-2">
      <p className="text-xs font-medium text-[var(--veritant-royal-70)]">Smart filter</p>
      <Input
        type="search"
        aria-label="Search the values"
        placeholder="Search…"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        className="h-7"
      />
      {/* One track the popover's width: an auto one took the longest value's width on one line,
          and every count went past the right edge, cut off by the scroll. */}
      <div
        role="group"
        aria-label="Values"
        className="grid max-h-56 grid-cols-[minmax(0,1fr)] gap-1.5 overflow-y-auto"
      >
        {read.state === 'reading' && <p className="text-muted-foreground">Counting…</p>}
        {read.state === 'failed' && (
          <div role="alert" className="grid gap-1">
            <p className="text-destructive">{read.message}</p>
            <Button
              type="button"
              variant="outline"
              className="h-7 w-fit px-2.5"
              onClick={() => setAttempt((n) => n + 1)}
            >
              Read again
            </Button>
          </div>
        )}
        {read.state === 'read' && (
          <>
            <label className="flex items-center gap-2 font-medium">
              <Checkbox
                checked={all}
                disabled={offered.length === 0 && !blankOffered}
                onCheckedChange={(next) => {
                  const on = next === true;
                  const rest = draft.values.filter((value) => !shownKeys.includes(keyOf(value)));
                  onDraft({
                    ...draft,
                    values: on ? [...rest, ...offered.map((facet) => facet.value)] : rest,
                    blank: blankOffered ? on : draft.blank
                  });
                }}
              />
              (Select all)
            </label>
            {facets.map((facet) => {
              const blank = facet.value === null;
              const label = blank ? '(Blank)' : labelOf(facet.value, facet.label);
              return (
                <label key={keyOf(facet.value)} className="flex items-start gap-2">
                  <Checkbox
                    className="mt-px"
                    aria-label={label}
                    checked={blank ? draft.blank : picked.has(keyOf(facet.value))}
                    onCheckedChange={(next) =>
                      blank ? onDraft({ ...draft, blank: next === true }) : toggle(facet.value, next === true)
                    }
                  />
                  <span className="min-w-0 flex-1 break-words">{label}</span>
                  <span className="shrink-0 pt-0.5 text-xs tabular-nums text-muted-foreground">
                    {facet.count.toLocaleString('en-US')}
                  </span>
                </label>
              );
            })}
            {strays.map((value) => {
              const label = labelOf(value, null);
              return (
                <label key={keyOf(value)} className="flex items-start gap-2">
                  <Checkbox className="mt-px" aria-label={label} checked onCheckedChange={() => toggle(value, false)} />
                  <span className="min-w-0 flex-1 break-words">{label}</span>
                </label>
              );
            })}
            {facets.length === 0 && strays.length === 0 && (
              <p className="text-muted-foreground">No value matches.</p>
            )}
            {read.facets.truncated && (
              <p className="text-xs text-muted-foreground">
                The 500 commonest values are shown — search to find another.
              </p>
            )}
          </>
        )}
      </div>
      <fieldset className="grid gap-1">
        <legend className="text-xs text-muted-foreground">Show the rows that</legend>
        {[
          { exclude: false, label: 'Include selected' },
          { exclude: true, label: 'Exclude selected' }
        ].map((option) => (
          <label key={option.label} className="flex items-center gap-2">
            <input
              type="radio"
              name={`funnel-${field.code}`}
              checked={draft.exclude === option.exclude}
              onChange={() => onDraft({ ...draft, exclude: option.exclude })}
              className="accent-[var(--color-teal)]"
            />
            {option.label}
          </label>
        ))}
      </fieldset>
    </div>
  );
}

type RangeState = Extract<FunnelState, { test: 'range' }>;

/** A bound's box, formatted as the column is — a dollar sign before money, a percent after. */
function Bound({
  label,
  kind,
  value,
  onChange
}: {
  label: string;
  kind: IListField['kind'];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="grid gap-1">
      {label}
      <span className="relative inline-flex items-center">
        {kind === 'money' && (
          <span aria-hidden className="pointer-events-none absolute left-2 text-muted-foreground">
            $
          </span>
        )}
        <Input
          inputMode="decimal"
          autoComplete="off"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className={cn('h-7 tabular-nums', kind === 'money' && 'pl-5', kind === 'percent' && 'pr-6')}
        />
        {kind === 'percent' && (
          <span aria-hidden className="pointer-events-none absolute right-2 text-muted-foreground">
            %
          </span>
        )}
      </span>
    </label>
  );
}

function RangeFilter({
  field,
  draft,
  onDraft
}: {
  field: IListField;
  draft: RangeState;
  onDraft: (state: RangeState) => void;
}) {
  return (
    <div className="grid gap-2">
      <p className="text-xs font-medium text-[var(--veritant-royal-70)]">Range</p>
      <Bound label="At least" kind={field.kind} value={draft.min} onChange={(min) => onDraft({ ...draft, min })} />
      <Bound label="At most" kind={field.kind} value={draft.max} onChange={(max) => onDraft({ ...draft, max })} />
    </div>
  );
}

type DateState = Extract<FunnelState, { test: 'date' }>;

const DATE_MODES: readonly { mode: DateState['mode']; label: string }[] = [
  { mode: 'since', label: 'Since' },
  { mode: 'before', label: 'Before' },
  { mode: 'between', label: 'Between' }
];

function DateFilter({ draft, onDraft }: { draft: DateState; onDraft: (state: DateState) => void }) {
  const name = React.useId();
  const between = draft.mode === 'between';
  return (
    <div className="grid gap-2">
      <p className="text-xs font-medium text-[var(--veritant-royal-70)]">Dates</p>
      <div role="radiogroup" aria-label="Dates" className="flex gap-3">
        {DATE_MODES.map((option) => (
          <label key={option.mode} className="flex items-center gap-1.5">
            <input
              type="radio"
              name={name}
              checked={draft.mode === option.mode}
              // "The cycle opened" is a since or a before; survey-api refuses it in a between.
              onChange={() =>
                onDraft({
                  ...draft,
                  mode: option.mode,
                  from: option.mode === 'between' && draft.from === 'cycle_open' ? '' : draft.from
                })
              }
              className="accent-[var(--color-teal)]"
            />
            {option.label}
          </label>
        ))}
      </div>
      <DateLiteral
        label={between ? 'From' : DATE_MODES.find((one) => one.mode === draft.mode)!.label}
        value={draft.from}
        allowCycle={!between}
        onChange={(from) => onDraft({ ...draft, from })}
      />
      {between && (
        <DateLiteral label="To" value={draft.to} allowCycle={false} onChange={(to) => onDraft({ ...draft, to })} />
      )}
    </div>
  );
}

type TextState = Extract<FunnelState, { test: 'text' }>;

/** A long text's test: an operator and one box, sent as typed — no values are read for it. */
function TextFilter({ draft, onDraft }: { draft: TextState; onDraft: (state: TextState) => void }) {
  const blank = operatorArity(draft.op) === 'none';
  return (
    <div className="grid gap-2">
      <p className="text-xs font-medium text-[var(--veritant-royal-70)]">Text</p>
      <Select value={draft.op} onValueChange={(op) => onDraft({ ...draft, op: op as TextFunnelOp })}>
        <SelectTrigger aria-label="Test" className="h-7">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {TEXT_FUNNEL_OPS.map((op) => (
            <SelectItem key={op} value={op}>
              {operatorLabel(op)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {!blank && (
        <Input
          aria-label="Text"
          autoComplete="off"
          value={draft.text}
          onChange={(event) => onDraft({ ...draft, text: event.target.value })}
          className="h-7"
        />
      )}
    </div>
  );
}
